import { COMBO_DEFINITIONS } from '../data/combos';
import type {
  ComboDefinition,
  DieFace,
  DieState,
  DiceTrayState,
  EligibleCombo,
} from './types';
import { getUnlockedDice } from './dice';

/**
 * Attack damage (combo stage only) =
 *   floor((weaponSwing + faceSum) × combo multiplier)
 *
 * Class flat Attack is NOT applied here (M7-D2 Stage B, approved option C).
 * Combat adds flat Attack after this floor and before Attack % bonuses.
 *
 * - Weapon swing: dungeon-entry equipment snapshot (0 when unarmed).
 * - Face values map 1:1 into the sum (roll a 2 → +2, roll a 6 → +6).
 * - Baseline Basic Attack: every unlocked die contributes its face.
 * - Named combos: only participating dice contribute (same set that can fire skills).
 * - Attack % bonuses apply later via applyOutgoingDamageBonuses (additive stacking).
 */
export function computeComboAttack(
  tray: DiceTrayState,
  definition: ComboDefinition,
  participatingDieIds: readonly number[],
  weaponSwingDamage = 0,
): { faceSum: number; multiplier: number; damage: number; weaponSwing: number } {
  const contributingIds =
    definition.id === 'baseline'
      ? getUnlockedDice(tray).map((die) => die.id)
      : participatingDieIds;

  let faceSum = 0;
  for (const id of contributingIds) {
    const die = tray.dice.find((d) => d.id === id);
    if (!die?.unlocked || die.face === undefined) {
      continue;
    }
    faceSum += die.face;
  }
  const multiplier = definition.multiplier;
  const weaponSwing = Math.max(0, Math.floor(weaponSwingDamage));
  return {
    faceSum,
    multiplier,
    weaponSwing,
    damage: Math.floor((weaponSwing + faceSum) * multiplier),
  };
}

export function computeComboDamage(
  tray: DiceTrayState,
  definition: ComboDefinition,
  participatingDieIds: readonly number[],
  weaponSwingDamage = 0,
): number {
  return computeComboAttack(
    tray,
    definition,
    participatingDieIds,
    weaponSwingDamage,
  ).damage;
}

/**
 * Primary-attack pre-% base (option C):
 *   comboDamage + classFlatAttack
 * Class flat is applied once; never combo-multiplied or OH-penalised.
 */
export function combineComboAndClassFlatAttack(
  comboDamage: number,
  classFlatAttack: number,
): number {
  return (
    Math.max(0, Math.floor(comboDamage)) +
    Math.max(0, Math.floor(classFlatAttack))
  );
}

/**
 * Tries to assign distinct face groups to satisfy a descending count pattern.
 * Returns participating die ids (ascending) or null if impossible.
 */
function matchPattern(
  dice: DieState[],
  pattern: number[],
): number[] | null {
  if (pattern.length === 0) {
    return [];
  }

  const byFace = new Map<DieFace, DieState[]>();
  for (const die of dice) {
    if (die.face === undefined) {
      continue;
    }
    const list = byFace.get(die.face) ?? [];
    list.push(die);
    byFace.set(die.face, list);
  }

  // Prefer lower face values when counts tie so selection is deterministic.
  const faces = [...byFace.keys()].sort((a, b) => a - b);
  const usedFaces = new Set<DieFace>();
  const participating: DieState[] = [];

  for (const need of pattern) {
    let chosen: DieFace | null = null;
    for (const face of faces) {
      if (usedFaces.has(face)) {
        continue;
      }
      const group = byFace.get(face);
      if (group && group.length >= need) {
        chosen = face;
        break;
      }
    }
    if (chosen === null) {
      return null;
    }
    usedFaces.add(chosen);
    const group = byFace.get(chosen)!;
    // Left-to-right / ascending id among the face group.
    const sorted = [...group].sort((a, b) => a.id - b.id);
    participating.push(...sorted.slice(0, need));
  }

  return participating.map((d) => d.id).sort((a, b) => a - b);
}

function matchPerfectStraight(dice: DieState[]): number[] | null {
  if (dice.length < 6) {
    return null;
  }
  const faces = new Set(dice.map((d) => d.face));
  if (faces.size !== 6) {
    return null;
  }
  for (let f = 1; f <= 6; f += 1) {
    if (!faces.has(f as DieFace)) {
      return null;
    }
  }
  // One die per face, lowest id for each face.
  const picked: number[] = [];
  for (let f = 1; f <= 6; f += 1) {
    const candidates = dice
      .filter((d) => d.face === f)
      .sort((a, b) => a.id - b.id);
    const first = candidates[0];
    if (!first) {
      return null;
    }
    picked.push(first.id);
  }
  return picked.sort((a, b) => a - b);
}

/**
 * Enumerates eligible combos for the current unlocked rolled faces.
 * Always includes baseline Basic Attack so a one-die turn can attack.
 */
export function findEligibleCombos(
  tray: DiceTrayState,
  weaponSwingDamage = 0,
  classFlatAttack = 0,
): EligibleCombo[] {
  const unlocked = getUnlockedDice(tray);
  if (unlocked.length === 0) {
    return [];
  }
  if (unlocked.some((d) => d.face === undefined)) {
    return [];
  }

  const results: EligibleCombo[] = [];
  const flat = Math.max(0, Math.floor(classFlatAttack));

  for (const definition of COMBO_DEFINITIONS) {
    if (definition.id === 'baseline') {
      // Basic Attack sums every unlocked face — those same dice also fire
      // equipped primary skills left→right (Cleave/Rend/etc. on a one-die turn).
      const allIds = unlocked.map((die) => die.id);
      const comboDmg = computeComboDamage(
        tray,
        definition,
        allIds,
        weaponSwingDamage,
      );
      results.push({
        definition,
        participatingDieIds: allIds,
        // Preview matches option C primary path (combo + class flat, pre-%).
        estimatedDamage: combineComboAndClassFlatAttack(comboDmg, flat),
      });
      continue;
    }

    const needed = definition.perfectStraight
      ? 6
      : definition.pattern.reduce((sum, n) => sum + n, 0);
    if (unlocked.length < needed) {
      continue;
    }

    const participating = definition.perfectStraight
      ? matchPerfectStraight(unlocked)
      : matchPattern(unlocked, definition.pattern);

    if (!participating) {
      continue;
    }

    results.push({
      definition,
      participatingDieIds: participating,
      estimatedDamage: combineComboAndClassFlatAttack(
        computeComboDamage(
          tray,
          definition,
          participating,
          weaponSwingDamage,
        ),
        flat,
      ),
    });
  }

  return results;
}

/**
 * Default combo: highest multiplier; ties break by definition list order
 * (already sorted strongest-first in COMBO_DEFINITIONS).
 * OPEN: exact tie-break policy is not finalized.
 */
export function chooseDefaultCombo(
  candidates: EligibleCombo[],
): EligibleCombo | null {
  if (candidates.length === 0) {
    return null;
  }
  let best = candidates[0]!;
  for (const candidate of candidates) {
    if (candidate.definition.multiplier > best.definition.multiplier) {
      best = candidate;
    }
  }
  return best;
}

export function getComboById(
  candidates: EligibleCombo[],
  comboId: string,
): EligibleCombo | null {
  return candidates.find((c) => c.definition.id === comboId) ?? null;
}
